import React, {useState, useEffect} from 'react';
import { buildSpectateUrl } from '~spa/Utils/Spectate';
import { withTranslation } from 'react-i18next';
import { getJson } from '~shared/utils/http';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import ContentSection from '~shared/components/react/ContentSection/index';
import i18n from '~shared/utils/i18n';
import TextRow from '~shared/components/react/text-row/TextRow.jsx';
import RouterLink from '~shared/components/react/RouterLink/RouterLink.jsx';
import { getSportNameBySlug } from '~shared/utils/sportsbook';
import BetFeedWidget from '~scenes/betfeed/widget';
import NoMarkets from '~spa/Pages/ErrorPages/NoMarkets/NoMarkets';
import LoadingIndicator from '~shared/components/react/LoadingIndicator';
import './styles/multi-grouping.scss';
import {
    seoUrlInplayEvent,
    seoUrlTournamentOutrights,
    seoUrlEvent,
} from '~spa/Utils/SeoUrls.js';
import {
    MULTIGROUPING_MAIN,
    MULTIGROUPING_ENDPOINT,
    MULTIGROUPING_CONTENT_SECTION,
} from './constants';
import {
    I18N_EVENT_DETAILS_URL_HOMEPAGE,
    I18N_EVENT_DETAILS_URL_EVENT,
} from '~shared/components/sport-event/constants';

const MultiGrouping = (props) => {

    const {
        sportSlug,
    } = props.match.params;

    const [groupedData, setGroupedData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect (() => {
        const getMultiGroups = async () => {
            const response = await getJson(buildSpectateUrl(`${MULTIGROUPING_ENDPOINT}/${sportSlug}`));
            if (response && response.ok) {
                let jsonResponse = await response.json();
                setGroupedData(jsonResponse);
                setIsLoading(false);
            }
        };
        getMultiGroups();
      }, []);

    const sportName = getSportNameBySlug(sportSlug);

    /**
     * Returns the correct URL for an event
     * @param   {object} urlParams  data for the event urls
     * @param   {object} data  data for the event
     * @return  {string}       The URL data
     */
    const getContentURLs = (urlParams, data) => {
        const outrights = props.t('url.outrights');
        const INPLAY_EVENT_URL_PATH = `${i18n.t(I18N_EVENT_DETAILS_URL_HOMEPAGE)}/${i18n.t(I18N_EVENT_DETAILS_URL_EVENT)}`;
        const baseUrl = `${urlParams.sport_l10n_slug}/${urlParams.category_l10n_slug}/${data.tournament_l10n_slug}`;
        const TOURNAMENT_OUTRIGHT_URL_PATH = `${baseUrl}/${outrights}`;
        const PREPLAY_EVENT_URL_PATH = `${baseUrl}/${data.event_l10n_slug}`;

        if (data.live) {
            return seoUrlInplayEvent(`${INPLAY_EVENT_URL_PATH}/${data.event_id}`, data.event_id);
        } else if (data.tournament) {
            return seoUrlTournamentOutrights(TOURNAMENT_OUTRIGHT_URL_PATH, data.tournament_id);
        }

        return seoUrlEvent(PREPLAY_EVENT_URL_PATH, data.event_id);
    };

    /**
     * Returns the correct GTM attribute
     * @param   {object} sport  sportslug data for GTM
     * @param   {object} data  data for GTM
     * @return  {string}       The GTM data
     */
    const getGtmAttribute = (sport, data) => {
        let gtmSuffix;

        if (data.tournament) {
            gtmSuffix = data.tournament_slug;
        } else {
            gtmSuffix = data.event_slug;
        }

        return `${sport.sport_slug};homepage;${gtmSuffix}`;
    };

    /**
     * Injects the Content Section with Text row data into the multi-grouped sport page
     * @return  {object}  HTML node to be injected into the DOM of this sport page
     */
    const renderMultiGroupingContent = () => {

        if(isLoading) {
            return <LoadingIndicator />;
        }

        if(isEmpty(groupedData)) {
            return <NoMarkets />;
        }

        const categoriesView = groupedData.map((category) => {

            let urlParams = {
                category_l10n_slug: category.category_l10n_slug,
                sport_l10n_slug: category.sport_l10n_slug,
            };

            const contentView = category.data.map((content) => {

                const { tournament, tournament_id, tournament_name, event_id, event_name, live } = content;
                const contentId = tournament ? tournament_id : event_id;
                const contentName = tournament ? tournament_name : event_name;

                let params = {
                    id: contentId,
                    label: contentName,
                    tag: live || false,
                    tagLabel: i18n.t('event_schedule.live'),
                };

                const textRowItem = <TextRow key={ contentId } params={params}/>;

                return (
                    <RouterLink
                        key={ contentId }
                        dataShowBbLoader= { false }
                        gtmAttribute={getGtmAttribute(urlParams, content)}
                        url={getContentURLs(urlParams, content)}
                    >
                        { textRowItem }
                    </RouterLink>
                );
            });

            let contentSectionParams = {
                headerText: category.category_name,
                isCollapsible: true,
                classList: [ MULTIGROUPING_CONTENT_SECTION ],
                titleModifiers: ['sport', 'height-35'],
                dataTestId: category.category_id,
            };

            return (
                <ContentSection key={category.category_id} params={contentSectionParams}>
                    {contentView}
                </ContentSection>
            );
        });

        return categoriesView;
    };

    return (
        <div className={MULTIGROUPING_MAIN}>
            <BetFeedWidget
                sportSlug={sportSlug}
                sportName={sportName}
            />
            {renderMultiGroupingContent()}
        </div>
    );
};

MultiGrouping.propTypes = {
    match: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
};

const MultiGroup = withTranslation()(MultiGrouping);

export default MultiGroup;
